/**
 * DOM-path frame capture: rasterize a live DOM-rendered frame to PNG via an
 * SVG <foreignObject> snapshot. This is the universal fallback that makes
 * agent vision (export_frame) and project covers work in every browser — the
 * WICG canvas path only exists behind a Canary flag.
 *
 * Known v1 limits (accepted): cross-origin images without CORS render blank,
 * web fonts fall back to system fonts, shader effects don't appear (the DOM
 * view itself only shows CSS-filter approximations, so the capture matches
 * what the user sees).
 */

/** Visual properties inlined per element. A bounded allowlist — a full
 *  getComputedStyle dump is ~350 props and bloats the SVG 6×. */
const STYLE_PROPS = [
  // box + layout
  'display', 'position', 'top', 'left', 'right', 'bottom', 'z-index',
  'width', 'height', 'min-width', 'min-height', 'max-width', 'max-height',
  'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'box-sizing', 'overflow-x', 'overflow-y',
  'flex-direction', 'flex-wrap', 'flex-grow', 'flex-shrink', 'flex-basis',
  'grid-template-columns', 'grid-template-rows', 'grid-area', 'gap',
  'align-items', 'align-content', 'align-self',
  'justify-content', 'justify-items', 'justify-self',
  // text
  'font-family', 'font-size', 'font-weight', 'font-style', 'line-height',
  'letter-spacing', 'white-space', 'text-align', 'text-transform',
  'text-decoration', 'text-overflow', 'vertical-align', 'color',
  'list-style-type', 'list-style-position',
  // paint
  'background-color', 'background-image', 'background-size',
  'background-position', 'background-repeat',
  'border-top-width', 'border-right-width', 'border-bottom-width', 'border-left-width',
  'border-top-style', 'border-right-style', 'border-bottom-style', 'border-left-style',
  'border-top-color', 'border-right-color', 'border-bottom-color', 'border-left-color',
  'border-top-left-radius', 'border-top-right-radius',
  'border-bottom-left-radius', 'border-bottom-right-radius',
  'box-shadow', 'opacity', 'transform', 'transform-origin', 'filter',
  'object-fit', 'object-position', 'cursor', 'outline',
] as const;

/** CSS.escape with a jsdom-safe fallback (frame ids are shortIds, but agent
 *  input flows through here — escape quotes/backslashes regardless). */
function cssEscape(s: string): string {
  return typeof CSS !== 'undefined' && CSS.escape
    ? CSS.escape(s)
    : s.replace(/[\\"']/g, '\\$&');
}

/** The element DOM capture rasterizes: the frame's content region (html the
 *  designer authored), not the titlebar/handles chrome. */
export function captureRootFor(frameId: string): HTMLElement | null {
  const root = document.querySelector(`[data-frame-id="${cssEscape(frameId)}"]`);
  if (!root) return null;
  return (root.querySelector('.codeframe-content') as HTMLElement) ?? (root as HTMLElement);
}

/** Deep-clone `src` with each element's computed visual styles inlined, so the
 *  snapshot is stylesheet-independent. Never mutates the live tree. */
export function cloneWithInlineStyles(src: HTMLElement): HTMLElement {
  const clone = src.cloneNode(true) as HTMLElement;
  const srcWalk = collectElements(src);
  const dstWalk = collectElements(clone);
  for (let i = 0; i < srcWalk.length; i++) {
    const cs = getComputedStyle(srcWalk[i]);
    const style = STYLE_PROPS
      .map((p) => {
        const v = cs.getPropertyValue(p);
        return v ? `${p}:${v}` : '';
      })
      .filter(Boolean)
      .join(';');
    dstWalk[i].setAttribute('style', style);
  }
  return clone;
}

function collectElements(root: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [root];
  root.querySelectorAll('*').forEach((el) => {
    if (el instanceof HTMLElement) out.push(el);
  });
  return out;
}

/** Inline every <img> as a data URL so the SVG snapshot is self-contained.
 *  asset:// refs are already objectURLs/CDN at the render seam; blob:/same-
 *  origin fetch cleanly. Failures (cross-origin without CORS) are left as-is
 *  — they render blank in the capture, matching the v1 limit. */
export async function inlineImages(clone: HTMLElement): Promise<void> {
  const imgs = Array.from(clone.querySelectorAll('img'));
  await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute('src') ?? '';
      if (!src || src.startsWith('data:')) return;
      try {
        const blob = await (await fetch(src)).blob();
        img.setAttribute('src', await blobToDataUrl(blob));
      } catch {
        /* leave as-is — blank in capture */
      }
    }),
  );
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result as string);
    fr.onerror = () => rej(new Error('FileReader failed'));
    fr.readAsDataURL(blob);
  });
}

/** Wrap a style-inlined clone in a sized SVG foreignObject (XHTML island). */
export function buildCaptureSvg(clone: HTMLElement, w: number, h: number): string {
  const holder = document.createElementNS('http://www.w3.org/1999/xhtml', 'div');
  holder.appendChild(clone);
  const xhtml = new XMLSerializer().serializeToString(holder);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">` +
    `<foreignObject width="100%" height="100%">${xhtml}</foreignObject></svg>`
  );
}

/** Rasterize a DOM-rendered frame to PNG. Layout px (offsetWidth/Height —
 *  unaffected by the canvas zoom transform). */
export async function captureFrameDomPng(
  frameId: string,
): Promise<{ base64: string; w: number; h: number }> {
  const content = captureRootFor(frameId);
  if (!content) throw new Error(`frame "${frameId}" is not on the canvas — focus_view it first`);
  const w = Math.max(1, content.offsetWidth);
  const h = Math.max(1, content.offsetHeight || content.scrollHeight);

  const clone = cloneWithInlineStyles(content);
  await inlineImages(clone);
  const svg = buildCaptureSvg(clone, w, h);

  const img = new Image();
  img.decoding = 'sync';
  const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await new Promise<void>((res, rej) => {
    img.onload = () => res();
    img.onerror = () => rej(new Error('SVG snapshot failed to decode'));
    img.src = svgUrl;
  });

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  ctx.drawImage(img, 0, 0);
  const blob = await new Promise<Blob | null>((res) => out.toBlob(res, 'image/png'));
  if (!blob) throw new Error('capture toBlob returned null');
  const dataUrl = await blobToDataUrl(blob);
  return { base64: dataUrl.split(',')[1] ?? '', w, h };
}

/** Downscaled JPEG of the largest visible design frame — the cover fallback
 *  for DOM-mode projects (no <canvas> on screen). Null when nothing usable. */
export async function captureLargestFrameJpeg(maxEdge = 640): Promise<string | null> {
  try {
    const roots = Array.from(document.querySelectorAll('[data-frame-id]')) as HTMLElement[];
    const best = roots
      .map((r) => ({ r, area: r.offsetWidth * r.offsetHeight }))
      .filter((x) => x.area > 0)
      .sort((a, b) => b.area - a.area)[0];
    if (!best) return null;
    const id = best.r.getAttribute('data-frame-id')!;
    const { base64, w, h } = await captureFrameDomPng(id);

    const img = new Image();
    await new Promise<void>((res, rej) => {
      img.onload = () => res();
      img.onerror = () => rej(new Error('decode failed'));
      img.src = `data:image/png;base64,${base64}`;
    });
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(w * scale));
    out.height = Math.max(1, Math.round(h * scale));
    const ctx = out.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, out.width, out.height);
    return out.toDataURL('image/jpeg', 0.8);
  } catch {
    return null;
  }
}
